import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import type {
  VendorSearchResponse,
  VendorListResponse,
  CreateVendorRequest,
  CreateVendorResponse,
  TaxJurisdiction,
  VendorTaxComplianceProfile,
  VendorTaxComplianceReviewRequest,
  VendorTaxComplianceTaxDocRequest,
  VendorTaxFilingPackageResponse,
  VendorTaxReadinessReportResponse,
  VendorInvoiceRule,
  VendorInvoiceRuleListResponse,
  VendorInvoiceRuleRequest,
  VendorDuplicateCandidateDetail,
  VendorDuplicateCandidateListResponse,
  VendorDuplicateResolutionRequest,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

/**
 * Fetch paginated list of vendors.
 */
export const useVendorList = (
  skip: number = 0,
  limit: number = 20,
  searchTerm: string = '',
  erpConnectionId: string = '',
) => {
  const normalizedSearchTerm = searchTerm.trim();
  const erpConnectionParam = erpConnectionId || undefined;

  return useQuery({
    queryKey: ['vendors', 'list', normalizedSearchTerm, erpConnectionParam, skip, limit],
    queryFn: async () => {
      const { data } = normalizedSearchTerm
        ? await apiClient.get<VendorListResponse>('/vendors/search', {
            params: { search: normalizedSearchTerm, offset: skip, limit, erp_connection_id: erpConnectionParam },
          })
        : await apiClient.get<VendorListResponse>('/vendors', {
            params: { skip, limit, erp_connection_id: erpConnectionParam },
          });
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetch single vendor by ID (for edit view).
 */
export const useVendorById = (id: string) => {
  return useQuery({
    queryKey: ['vendors', id],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorSearchResponse>(`/vendors/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

/**
 * Create a new vendor.
 */
export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: CreateVendorRequest) => {
      const { data } = await apiClient.post<CreateVendorResponse>('/vendors', request);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({
        message: 'Vendor created successfully',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to create vendor',
        type: 'error',
      });
    },
  });
};

/**
 * Update an existing vendor.
 */
export const useUpdateVendor = (vendorId: string) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: Partial<CreateVendorRequest>) => {
      const { data } = await apiClient.put<VendorSearchResponse>(
        `/vendors/${vendorId}`,
        request
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId] });
      addNotification({
        message: 'Vendor updated successfully',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to update vendor',
        type: 'error',
      });
    },
  });
};

/**
 * Delete a vendor by ID.
 */
export const useDeleteVendor = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (vendorId: string) => {
      await apiClient.delete(`/vendors/${vendorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({
        message: 'Vendor deleted successfully',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to delete vendor',
        type: 'error',
      });
    },
  });
};

/**
 * Push an existing local vendor to ERP when no external vendor ID is linked.
 */
export const usePushVendorToErp = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({ vendorId, erpConnectionId }: { vendorId: string; erpConnectionId?: string }) => {
      const { data } = await apiClient.post<VendorSearchResponse>(
        `/vendors/${vendorId}/push-to-erp`,
        undefined,
        { params: { erp_connection_id: erpConnectionId } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({
        message: 'Vendor pushed to ERP successfully',
        type: 'success',
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to push vendor to ERP',
        type: 'error',
      });
    },
  });
};

export const useVendorDuplicateCandidates = (status: 'needs_review' | 'resolved' = 'needs_review', enabled = true) => {
  return useQuery({
    queryKey: ['vendors', 'duplicate-candidates', status],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorDuplicateCandidateListResponse>(
        '/vendors/duplicate-candidates',
        { params: { status } },
      );
      return data;
    },
    enabled,
  });
};

export const useVendorDuplicateCandidate = (candidateId?: string) => {
  return useQuery({
    queryKey: ['vendors', 'duplicate-candidates', candidateId],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorDuplicateCandidateDetail>(
        `/vendors/duplicate-candidates/${candidateId}`,
      );
      return data;
    },
    enabled: Boolean(candidateId),
  });
};

export const useResolveVendorDuplicateCandidate = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({ candidateId, payload }: { candidateId: string; payload: VendorDuplicateResolutionRequest }) => {
      const { data } = await apiClient.post<VendorDuplicateCandidateDetail>(
        `/vendors/duplicate-candidates/${candidateId}/resolve`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['erp-connections'] });
      queryClient.invalidateQueries({ queryKey: ['vendors', 'duplicate-candidates'] });
      addNotification({ message: 'Vendor duplicate candidate resolved', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to resolve vendor duplicate candidate',
        type: 'error',
      });
    },
  });
};

export const useVendorInvoiceRules = (vendorId?: string) => {
  return useQuery({
    queryKey: ['vendors', vendorId, 'invoice-rules'],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorInvoiceRuleListResponse>(
        `/vendors/${vendorId}/invoice-rules`
      );
      return data;
    },
    enabled: Boolean(vendorId),
  });
};

export const useUpsertVendorInvoiceRule = (vendorId: string) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: VendorInvoiceRuleRequest) => {
      const { data } = await apiClient.post<VendorInvoiceRule>(
        `/vendors/${vendorId}/invoice-rules`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'invoice-rules'] });
      addNotification({ message: 'Vendor invoice rule saved', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to save vendor invoice rule',
        type: 'error',
      });
    },
  });
};

export const useUpdateVendorInvoiceRule = (vendorId: string) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      ruleId,
      payload,
    }: {
      ruleId: string;
      payload: Partial<VendorInvoiceRuleRequest>;
    }) => {
      const { data } = await apiClient.put<VendorInvoiceRule>(
        `/vendors/${vendorId}/invoice-rules/${ruleId}`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'invoice-rules'] });
      addNotification({ message: 'Vendor invoice rule updated', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to update vendor invoice rule',
        type: 'error',
      });
    },
  });
};

export const useDeleteVendorInvoiceRule = (vendorId: string) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (ruleId: string) => {
      await apiClient.delete(`/vendors/${vendorId}/invoice-rules/${ruleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'invoice-rules'] });
      addNotification({ message: 'Vendor invoice rule deleted', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to delete vendor invoice rule',
        type: 'error',
      });
    },
  });
};

export const useVendorTaxReadinessReport = (
  filingYear?: number,
  jurisdiction?: TaxJurisdiction,
  notify: boolean = false,
) => {
  return useQuery({
    queryKey: ['vendors', 'tax-compliance', 'readiness-report', filingYear, jurisdiction, notify],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorTaxReadinessReportResponse>(
        '/vendors/tax-compliance/readiness-report',
        { params: { filing_year: filingYear, jurisdiction, notify } }
      );
      return data;
    },
    staleTime: 60 * 1000,
  });
};

export const useVendorTaxFilingPackage = (
  filingYear?: number,
  jurisdiction?: TaxJurisdiction,
  includeUnreviewed: boolean = false,
) => {
  return useQuery({
    queryKey: ['vendors', 'tax-compliance', 'filing-package', filingYear, jurisdiction, includeUnreviewed],
    queryFn: async () => {
      const { data } = await apiClient.get<VendorTaxFilingPackageResponse>(
        '/vendors/tax-compliance/filing-package',
        { params: { filing_year: filingYear, jurisdiction, include_unreviewed: includeUnreviewed } }
      );
      return data;
    },
    staleTime: 60 * 1000,
  });
};

export const useReassessVendorTaxCompliance = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      vendorId,
      filingYear,
      jurisdiction,
    }: {
      vendorId: string;
      filingYear?: number;
      jurisdiction?: TaxJurisdiction;
    }) => {
      const { data } = await apiClient.post<VendorTaxComplianceProfile>(
        `/vendors/${vendorId}/tax-compliance/reassess`,
        null,
        { params: { filing_year: filingYear, jurisdiction } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({ message: '1099 assessment refreshed', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to refresh 1099 assessment',
        type: 'error',
      });
    },
  });
};

export const useReviewVendorTaxCompliance = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      vendorId,
      filingYear,
      payload,
    }: {
      vendorId: string;
      filingYear?: number;
      // `jurisdiction` and CA-specific fields live on the request body
      // (see VendorTaxComplianceReviewRequest).
      payload: VendorTaxComplianceReviewRequest;
    }) => {
      const { data } = await apiClient.post<VendorTaxComplianceProfile>(
        `/vendors/${vendorId}/tax-compliance/review`,
        payload,
        { params: { filing_year: filingYear } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({ message: '1099 review saved', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to save 1099 review',
        type: 'error',
      });
    },
  });
};

export const useRequestVendorTaxDoc = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      vendorId,
      filingYear,
      payload,
    }: {
      vendorId: string;
      filingYear?: number;
      // `jurisdiction` already lives on VendorTaxComplianceTaxDocRequest body.
      payload: VendorTaxComplianceTaxDocRequest;
    }) => {
      const { data } = await apiClient.post<VendorTaxComplianceProfile>(
        `/vendors/${vendorId}/tax-compliance/request-tax-doc`,
        payload,
        { params: { filing_year: filingYear } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      addNotification({ message: 'Tax document request marked', type: 'success' });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to mark tax document request',
        type: 'error',
      });
    },
  });
};
